type AppUser = {
  role: 'ADMIN' | 'CLIENT' | 'EMPLOYEE' | 'PARTNER';
  profile_role?: string;
};

const HOSTS = {
  public: (import.meta.env.VITE_PUBLIC_HOST as string | undefined)?.trim() || '',
  admin: (import.meta.env.VITE_ADMIN_HOST as string | undefined)?.trim() || '',
  client: (import.meta.env.VITE_CLIENT_HOST as string | undefined)?.trim() || '',
  support: (import.meta.env.VITE_SUPPORT_HOST as string | undefined)?.trim() || '',
  partner: (import.meta.env.VITE_PARTNER_HOST as string | undefined)?.trim() || '',
  employee: (import.meta.env.VITE_EMPLOYEE_HOST as string | undefined)?.trim() || '',
};

function isAbsoluteUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function extractHostname(hostOrUrl: string): string {
  const value = hostOrUrl.trim();
  if (!value) return '';

  if (isAbsoluteUrl(value)) {
    try {
      return new URL(value).hostname.toLowerCase();
    } catch {
      return '';
    }
  }

  return value.split(':')[0].toLowerCase();
}

function getCurrentHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.toLowerCase();
}

function buildUrlFromHost(hostOrUrl: string, path: string): string {
  if (isAbsoluteUrl(hostOrUrl)) {
    const base = new URL(hostOrUrl);
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${base.origin}${normalizedPath}`;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  return `${protocol}//${hostOrUrl}${normalizedPath}`;
}

export function getDefaultPathForUser(user: AppUser): string {
  if (user.role === 'ADMIN') return '/admin';
  if (user.role === 'CLIENT') return '/portal/client';
  if (user.role === 'PARTNER') return '/portal/prm';
  return '/admin';
}

export function getHostForUser(user: AppUser): string {
  if (user.role === 'CLIENT') return HOSTS.client || HOSTS.public;
  if (user.role === 'PARTNER') return HOSTS.partner || HOSTS.public;
  if (user.role === 'ADMIN') return HOSTS.admin || HOSTS.public;

  const profileRole = (user.profile_role || '').toLowerCase();
  if (profileRole.includes('support')) return HOSTS.support || HOSTS.admin || HOSTS.public;
  if (profileRole.includes('project')) return HOSTS.employee || HOSTS.admin || HOSTS.public;
  return HOSTS.employee || HOSTS.admin || HOSTS.public;
}

export function getUserDestinationUrl(user: AppUser, path?: string): string | null {
  const targetHost = getHostForUser(user);
  if (!targetHost) return null;
  return buildUrlFromHost(targetHost, path || getDefaultPathForUser(user));
}

export function getPublicUrl(path = '/'): string | null {
  if (!HOSTS.public) return null;
  return buildUrlFromHost(HOSTS.public, path);
}

export function isRestrictedHost(): boolean {
  const current = getCurrentHostname();
  if (!current) return false;

  const restrictedHosts = [HOSTS.admin, HOSTS.client, HOSTS.support, HOSTS.partner, HOSTS.employee]
    .map(extractHostname)
    .filter(Boolean);

  return restrictedHosts.includes(current);
}

export function isExternalToCurrentOrigin(targetUrl: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(targetUrl, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
}
